"use client";

// In-memory, session-scoped cache for a patient's prescriptions.
//
// Prescriptions are sensitive personal health information, so this cache is
// deliberately conservative (PIPEDA safeguards / limiting retention):
//   - it lives ONLY in browser memory — never localStorage/sessionStorage or
//     any other on-disk store,
//   - it is keyed by token + patientId so a different account signed in on the
//     same browser can never read another user's data,
//   - entries expire after a short TTL, and
//   - it is wiped on logout via clearPrescriptionsCache().
//
// The "use client" directive keeps this module out of the server bundle, so
// the Map is never shared across requests/users on the server.

import { fetchPrescriptions, type Prescription } from "@/lib/api/customer";

const CACHE_TTL_MS = 2 * 60 * 1000;

type CacheEntry = { fetchedAt: number; data: Prescription[] };

const cache = new Map<string, CacheEntry>();
const inflight = new Map<string, Promise<PrescriptionsResult>>();

export type PrescriptionsResult =
  | { ok: true; data: Prescription[] }
  | { ok: false; error: string };

function keyOf(token: string, patientId: string) {
  return `${token}|${patientId}`;
}

/** Any cached data, even if stale — for instant paint while revalidating. */
export function peekPrescriptions(
  token?: string | null,
  patientId?: string | null,
): Prescription[] | null {
  if (!token || !patientId) return null;
  return cache.get(keyOf(token, patientId))?.data ?? null;
}

/** True when cached data exists and is still within the TTL. */
export function hasFreshPrescriptions(
  token?: string | null,
  patientId?: string | null,
): boolean {
  if (!token || !patientId) return false;
  const entry = cache.get(keyOf(token, patientId));
  return !!entry && Date.now() - entry.fetchedAt < CACHE_TTL_MS;
}

/**
 * Fetch prescriptions, deduping concurrent callers and caching successes.
 * Errors are surfaced (not swallowed) so callers can show an error state;
 * failed responses are never cached.
 */
export function loadPrescriptions(
  token: string,
  patientId: string,
): Promise<PrescriptionsResult> {
  const key = keyOf(token, patientId);
  const existing = inflight.get(key);
  if (existing) return existing;

  const req = fetchPrescriptions(token, patientId)
    .then((res): PrescriptionsResult => {
      if (res.ok) {
        cache.set(key, { data: res.data.prescriptions, fetchedAt: Date.now() });
        return { ok: true, data: res.data.prescriptions };
      }
      return { ok: false, error: res.error };
    })
    .catch((err): PrescriptionsResult => ({
      ok: false,
      error: err instanceof Error ? err.message : "Could not load prescriptions.",
    }))
    .finally(() => {
      inflight.delete(key);
    });

  inflight.set(key, req);
  return req;
}

/** Wipe all cached prescriptions. Call on logout / account switch. */
export function clearPrescriptionsCache() {
  cache.clear();
  inflight.clear();
}
